import React, { useState } from 'react';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
import { DashboardSidebar } from '../components/dashboard/DashboardSidebar';
import { BellIcon, PlusIcon, EditIcon, TrashIcon, AlertCircleIcon, CheckCircleIcon, PauseCircleIcon, CalendarIcon, TagIcon, GlobeIcon, SettingsIcon, FilterIcon, RefreshCwIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { CreateAlertModal } from '../components/alerts/CreateAlertModal';
import { EditAlertModal } from '../components/alerts/EditAlertModal';
export function AlertsDashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [currentAlert, setCurrentAlert] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  // Mock user data
  const user = {
    name: 'FATHALLAH Mehdi',
    company: 'Tunipages',
    avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
    email: 'mehdi.fathallah@tunipages.com',
    phone: '+216 71 234 567'
  };
  // Mock alerts data
  const [alerts, setAlerts] = useState([{
    id: 1,
    name: "Projets d'infrastructure au Maroc",
    keywords: ['infrastructure', 'construction', 'routes'],
    countries: ['Maroc'],
    frequency: 'Instantanée',
    status: 'active',
    createdAt: '2023-05-10',
    lastNotification: '2023-06-02',
    matchCount: 12,
    newMatches: 3
  }, {
    id: 2,
    name: "Fourniture d'équipements médicaux",
    keywords: ['médical', 'équipement', 'hôpital'],
    countries: ['Sénégal', "Côte d'Ivoire"],
    frequency: 'Quotidienne',
    status: 'active',
    createdAt: '2023-05-15',
    lastNotification: '2023-06-01',
    matchCount: 8,
    newMatches: 0
  }, {
    id: 3,
    name: 'Projets énergétiques renouvelables',
    keywords: ['énergie', 'solaire', 'renouvelable'],
    countries: ['Tunisie', 'Algérie', 'Maroc'],
    frequency: 'Hebdomadaire',
    status: 'paused',
    createdAt: '2023-04-20',
    lastNotification: '2023-05-25',
    matchCount: 15,
    newMatches: 0
  }, {
    id: 4,
    name: 'Services de conseil en technologie',
    keywords: ['conseil', 'technologie', 'numérique'],
    countries: ['Afrique du Sud', 'Kenya', 'Nigeria'],
    frequency: 'Quotidienne',
    status: 'active',
    createdAt: '2023-05-28',
    lastNotification: '2023-06-02',
    matchCount: 5,
    newMatches: 2
  }, {
    id: 5,
    name: 'Projets agricoles et agroalimentaires',
    keywords: ['agriculture', 'agroalimentaire', 'irrigation'],
    countries: ["Côte d'Ivoire", 'Ghana', 'Cameroun'],
    frequency: 'Hebdomadaire',
    status: 'expired',
    createdAt: '2023-03-15',
    lastNotification: '2023-05-15',
    matchCount: 23,
    newMatches: 0
  }]);
  const handleEditAlert = alert => {
    setCurrentAlert(alert);
    setShowEditModal(true);
  };
  const handleSaveEdit = updatedAlert => {
    setAlerts(alerts.map(alert => alert.id === updatedAlert.id ? updatedAlert : alert));
    setShowEditModal(false);
    setCurrentAlert(null);
  };
  const handleCreateAlert = newAlert => {
    const newAlertWithId = {
      ...newAlert,
      id: alerts.length + 1,
      createdAt: new Date().toISOString().split('T')[0],
      status: 'active',
      matchCount: 0,
      newMatches: 0
    };
    setAlerts([...alerts, newAlertWithId]);
    setShowCreateModal(false);
  };
  const filteredAlerts = alerts.filter(alert => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'active') return alert.status === 'active';
    if (activeFilter === 'paused') return alert.status === 'paused';
    if (activeFilter === 'expired') return alert.status === 'expired';
    if (activeFilter === 'new') return alert.newMatches > 0;
    return true;
  });
  // Alert statistics
  const alertStats = {
    total: alerts.length,
    active: alerts.filter(a => a.status === 'active').length,
    paused: alerts.filter(a => a.status === 'paused').length,
    newMatches: alerts.reduce((sum, alert) => sum + alert.newMatches, 0),
    totalMatches: alerts.reduce((sum, alert) => sum + alert.matchCount, 0)
  };
  const getStatusIcon = (status: string) => {
    if (status === 'active') return <CheckCircleIcon size={16} className="text-green-500" data-id="element-1143" />;
    if (status === 'paused') return <PauseCircleIcon size={16} className="text-yellow-500" data-id="element-1144" />;
    if (status === 'expired') return <AlertCircleIcon size={16} className="text-red-500" data-id="element-1145" />;
    return null;
  };
  const getStatusText = (status: string) => {
    if (status === 'active') return 'Actif';
    if (status === 'paused') return 'En pause';
    if (status === 'expired') return 'Expiré';
    return status;
  };
  const getStatusClass = (status: string) => {
    if (status === 'active') return 'bg-green-100 text-green-800';
    if (status === 'paused') return 'bg-yellow-100 text-yellow-800';
    if (status === 'expired') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-1146">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && <div className="fixed inset-0 bg-gray-800 bg-opacity-50 z-20 md:hidden" onClick={() => setSidebarOpen(false)} data-id="element-1147" />}
      {/* Sidebar */}
      <DashboardSidebar open={sidebarOpen} setOpen={setSidebarOpen} data-id="element-1148" />
      {/* Main content */}
      <div className="md:pl-64" data-id="element-1149">
        {/* Header */}
        <DashboardHeader user={user} onMenuClick={() => setSidebarOpen(true)} data-id="element-1150" />
        {/* Main content */}
        <main className="py-6 px-4 sm:px-6 lg:px-8" data-id="element-1151">
          {/* Page header */}
          <div className="mb-6" data-id="element-1152">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-1153">
              <div className="flex items-center mb-4 md:mb-0" data-id="element-1154">
                <BellIcon size={24} className="text-blue-600 mr-3" data-id="element-1155" />
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-1156">
                  Mes Alertes
                </h1>
              </div>
              <Button variant="primary" onClick={() => setShowCreateModal(true)} className="flex items-center justify-center shadow-lg shadow-yellow-500/20" data-id="element-1157">
                <PlusIcon size={16} className="mr-2" data-id="element-1158" />
                Créer une alerte
              </Button>
            </div>
            <p className="mt-2 text-gray-600" data-id="element-1159">
              Configurez des alertes pour être notifié des nouveaux appels
              d'offres correspondant à vos critères.
            </p>
          </div>
          {/* Stats cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-1160">
            <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100" data-id="element-1161">
              <div className="flex items-center" data-id="element-1162">
                <div className="p-3 rounded-lg bg-blue-100 text-blue-600 mr-4" data-id="element-1163">
                  <BellIcon size={20} data-id="element-1164" />
                </div>
                <div data-id="element-1165">
                  <p className="text-sm font-medium text-gray-500" data-id="element-1166">
                    Total des alertes
                  </p>
                  <p className="text-xl font-semibold text-gray-900" data-id="element-1167">
                    {alertStats.total}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100" data-id="element-1168">
              <div className="flex items-center" data-id="element-1169">
                <div className="p-3 rounded-lg bg-green-100 text-green-600 mr-4" data-id="element-1170">
                  <CheckCircleIcon size={20} data-id="element-1171" />
                </div>
                <div data-id="element-1172">
                  <p className="text-sm font-medium text-gray-500" data-id="element-1173">
                    Alertes actives
                  </p>
                  <p className="text-xl font-semibold text-gray-900" data-id="element-1174">
                    {alertStats.active}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100" data-id="element-1175">
              <div className="flex items-center" data-id="element-1176">
                <div className="p-3 rounded-lg bg-red-100 text-red-600 mr-4" data-id="element-1177">
                  <AlertCircleIcon size={20} data-id="element-1178" />
                </div>
                <div data-id="element-1179">
                  <p className="text-sm font-medium text-gray-500" data-id="element-1180">
                    Nouveaux résultats
                  </p>
                  <p className="text-xl font-semibold text-gray-900" data-id="element-1181">
                    {alertStats.newMatches}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100" data-id="element-1182">
              <div className="flex items-center" data-id="element-1183">
                <div className="p-3 rounded-lg bg-purple-100 text-purple-600 mr-4" data-id="element-1184">
                  <TagIcon size={20} data-id="element-1185" />
                </div>
                <div data-id="element-1186">
                  <p className="text-sm font-medium text-gray-500" data-id="element-1187">
                    Total des résultats
                  </p>
                  <p className="text-xl font-semibold text-gray-900" data-id="element-1188">
                    {alertStats.totalMatches}
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* Filter tabs */}
          <div className="bg-white rounded-lg shadow-md mb-6 p-4 border border-gray-100" data-id="element-1189">
            <div className="flex items-center justify-between mb-4" data-id="element-1190">
              <div className="flex items-center" data-id="element-1191">
                <FilterIcon size={18} className="text-gray-500 mr-2" data-id="element-1192" />
                <h3 className="font-medium text-gray-700" data-id="element-1193">
                  Filtrer les alertes
                </h3>
              </div>
              <Button variant="secondary" size="sm" className="text-xs flex items-center" data-id="element-1194">
                <RefreshCwIcon size={14} className="mr-1" data-id="element-1195" />
                Actualiser
              </Button>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-1196">
              <button className={`px-3 py-1.5 rounded-full text-sm font-medium ${activeFilter === 'all' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`} onClick={() => setActiveFilter('all')} data-id="element-1197">
                Toutes ({alerts.length})
              </button>
              <button className={`px-3 py-1.5 rounded-full text-sm font-medium ${activeFilter === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`} onClick={() => setActiveFilter('active')} data-id="element-1198">
                Actives ({alertStats.active})
              </button>
              <button className={`px-3 py-1.5 rounded-full text-sm font-medium ${activeFilter === 'paused' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`} onClick={() => setActiveFilter('paused')} data-id="element-1199">
                En pause ({alertStats.paused})
              </button>
              <button className={`px-3 py-1.5 rounded-full text-sm font-medium ${activeFilter === 'expired' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`} onClick={() => setActiveFilter('expired')} data-id="element-1200">
                Expirées ({alerts.filter(a => a.status === 'expired').length})
              </button>
              <button className={`px-3 py-1.5 rounded-full text-sm font-medium ${activeFilter === 'new' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`} onClick={() => setActiveFilter('new')} data-id="element-1201">
                Nouveaux résultats (
                {alerts.filter(a => a.newMatches > 0).length})
              </button>
            </div>
          </div>
          {/* Alerts list */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100" data-id="element-1202">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-1203">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-1204">
                Liste des alertes
              </h2>
            </div>
            {filteredAlerts.length === 0 ? <div className="py-12 px-6 text-center" data-id="element-1205">
                <BellIcon size={48} className="mx-auto text-gray-300 mb-4" data-id="element-1206" />
                <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-1207">
                  Aucune alerte trouvée
                </h3>
                <p className="text-gray-500 mb-6" data-id="element-1208">
                  Vous n'avez pas encore créé d'alertes correspondant à ce
                  filtre.
                </p>
                <Button variant="primary" onClick={() => setShowCreateModal(true)} className="inline-flex items-center justify-center shadow-md" data-id="element-1209">
                  <PlusIcon size={16} className="mr-2" data-id="element-1210" />
                  Créer une alerte
                </Button>
              </div> : <ul className="divide-y divide-gray-200" data-id="element-1211">
                {filteredAlerts.map(alert => <li key={alert.id} className="px-6 py-5 hover:bg-gray-50 transition-colors" data-id="element-1212">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-1213">
                      <div className="mb-4 md:mb-0" data-id="element-1214">
                        <div className="flex items-center" data-id="element-1215">
                          {alert.newMatches > 0 && <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-100 text-red-800 text-xs font-medium mr-2" data-id="element-1216">
                              {alert.newMatches}
                            </span>}
                          <h3 className="text-lg font-medium text-blue-900" data-id="element-1217">
                            {alert.name}
                          </h3>
                          <span className={`ml-3 px-2.5 py-0.5 rounded-full text-xs font-medium inline-flex items-center ${getStatusClass(alert.status)}`} data-id="element-1218">
                            {getStatusIcon(alert.status)}
                            <span className="ml-1" data-id="element-1219">
                              {getStatusText(alert.status)}
                            </span>
                          </span>
                        </div>
                        <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2" data-id="element-1220">
                          <div className="flex items-center text-sm text-gray-500" data-id="element-1221">
                            <TagIcon size={14} className="mr-1.5 text-gray-400 flex-shrink-0" data-id="element-1222" />
                            <span className="truncate" data-id="element-1223">
                              {alert.keywords.join(', ')}
                            </span>
                          </div>
                          <div className="flex items-center text-sm text-gray-500" data-id="element-1224">
                            <GlobeIcon size={14} className="mr-1.5 text-gray-400 flex-shrink-0" data-id="element-1225" />
                            <span className="truncate" data-id="element-1226">
                              {alert.countries.join(', ')}
                            </span>
                          </div>
                          <div className="flex items-center text-sm text-gray-500" data-id="element-1227">
                            <CalendarIcon size={14} className="mr-1.5 text-gray-400 flex-shrink-0" data-id="element-1228" />
                            <span data-id="element-1229">Créée le {alert.createdAt}</span>
                          </div>
                        </div>
                        <div className="mt-2 flex items-center text-sm text-gray-500" data-id="element-1230">
                          <BellIcon size={14} className="mr-1.5 text-gray-400 flex-shrink-0" data-id="element-1231" />
                          <span data-id="element-1232">Fréquence: {alert.frequency}</span>
                          {alert.lastNotification && <span className="ml-4" data-id="element-1233">
                              Dernière notification: {alert.lastNotification}
                            </span>}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2" data-id="element-1234">
                        <button className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-full" onClick={() => handleEditAlert(alert)} data-id="element-1235">
                          <EditIcon size={18} data-id="element-1236" />
                        </button>
                        {alert.status === 'active' ? <button className="p-2 text-gray-500 hover:text-yellow-600 hover:bg-yellow-50 rounded-full" data-id="element-1237">
                            <PauseCircleIcon size={18} data-id="element-1238" />
                          </button> : alert.status === 'paused' ? <button className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-full" data-id="element-1239">
                            <CheckCircleIcon size={18} data-id="element-1240" />
                          </button> : null}
                        <button className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full" data-id="element-1241">
                          <TrashIcon size={18} data-id="element-1242" />
                        </button>
                        <Button variant="outline" size="sm" className="hidden md:inline-flex" data-id="element-1243">
                          Voir les résultats
                          {alert.newMatches > 0 && <span className="ml-2 bg-red-100 text-red-800 text-xs font-semibold px-2 py-0.5 rounded-full" data-id="element-1244">
                              {alert.newMatches} nouveaux
                            </span>}
                        </Button>
                      </div>
                    </div>
                  </li>)}
              </ul>}
          </div>
          {/* Upgrade banner */}
          <div className="mt-8 bg-blue-900 rounded-xl p-6 text-white shadow-lg relative overflow-hidden" data-id="element-1245">
            <div className="absolute inset-0 opacity-10" data-id="element-1246">
              <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-1247">
                <defs data-id="element-1248">
                  <pattern id="smallGrid" width="20" height="20" patternUnits="userSpaceOnUse" data-id="element-1249">
                    <path d="M 20 0 L 0 0 0 20" fill="none" stroke="white" strokeWidth="0.5" data-id="element-1250" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#smallGrid)" data-id="element-1251" />
              </svg>
            </div>
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between" data-id="element-1252">
              <div className="mb-4 md:mb-0" data-id="element-1253">
                <h3 className="text-xl font-bold mb-2" data-id="element-1254">
                  Besoin de plus d'alertes ?
                </h3>
                <p className="text-blue-100 max-w-2xl" data-id="element-1255">
                  Passez à notre forfait Premium pour créer des alertes
                  illimitées, accéder à des filtres avancés et recevoir des
                  notifications en temps réel.
                </p>
              </div>
              <Button variant="primary" className="shadow-lg shadow-yellow-500/20 whitespace-nowrap" data-id="element-1256">
                Passer au Premium
              </Button>
            </div>
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-blue-700 rounded-full opacity-30 transform translate-x-1/3 -translate-y-1/3" data-id="element-1257"></div>
            <div className="absolute bottom-0 left-0 w-20 h-20 bg-yellow-500 rounded-full opacity-20 transform -translate-x-1/2 translate-y-1/2" data-id="element-1258"></div>
          </div>
        </main>
      </div>
      {/* Create Alert Modal */}
      {showCreateModal && <CreateAlertModal onClose={() => setShowCreateModal(false)} onSave={handleCreateAlert} data-id="element-1259" />}
      {/* Edit Alert Modal */}
      {showEditModal && currentAlert && <EditAlertModal alert={currentAlert} onClose={() => {
      setShowEditModal(false);
      setCurrentAlert(null);
    }} onSave={handleSaveEdit} data-id="element-1260" />}
    </div>;
}